/**
 * Search Store - Alpine.js store for managing search providers and results
 * Follows the cart store pattern for consistent state management
 */
import { createProvider } from '@js/providers/providerFactory.js';

export default {
  name: 'search',
  store() {
    return {
      // Provider management
      provider: null,
      currentProviderName: 'shopify',
      providerLoadPromise: null,
      
      // Search state
      query: '',
      results: [],
      filters: [],
      appliedFilters: [],
      preserveFiltersDuringApply: false,
      
      // Category results (collections and pages)
      categoryResults: {
        collections: [],
        pages: []
      },
      
      // Category pagination state
      categoryPagination: {
        collections: {
          cursor: null,
          hasNextPage: false,
          total: 0,
          totalPages: 1,
          currentPage: 1
        },
        pages: {
          cursor: null,
          hasNextPage: false,
          total: 0,
          totalPages: 1,
          currentPage: 1
        }
      },
      
      // Predictive search state
      predictiveQuery: '',
      predictiveResults: {
        products: [],
        collections: [],
        queries: [],
        articles: [],
        pages: []
      },
      predictiveLoading: false,
      predictiveTotalResults: 0,
      predictiveRequestId: 0,
      predictiveActiveRequestId: 0,
      predictiveAbortController: null,

      // Default results — pre-fetched on page load using the configured default query
      defaultResults: {
        queries: [],
        collections: [],
        pages: []
      },
      defaultResultsLoading: false,
      defaultResultsQuery: '',
      defaultResultsPromise: null,
      defaultResultsDeferredPromise: null,
      defaultResultsPrefetchScheduled: false,
      
      // Modular predictive search configuration (seeded from global theme settings in head-scripts)
      predictiveEnabledTypes: Array.isArray(window.search?.predictiveEnabledTypes)
        ? window.search.predictiveEnabledTypes
        : ['queries', 'products', 'collections'],
      
      // Collection state
      collectionHandle: '',
      collectionInfo: null,
      isCollectionMode: false,
      collectionBanners: [],
      
      // UI state
      loading: false,
      loadingMore: false,
      isPaginating: false,
      error: null,
      activeTab: 'products',
      searchRequestId: 0,
      activeSearchRequestId: 0,
      activeSearchKey: '',
      activeSearchPromise: null,
      isSearchRedirecting: false,
      _categoryResultsStale: false,
      _categoryResultsLoading: false,
      _categoryResultsDeferredTimer: null,
      _filtersLoading: false,
      _filtersDeferredTimer: null,
      
      // Pagination
      cursor: null,
      hasNextPage: false,
      total: 0,
      currentPage: 1,
      restoredPage: null,
      
      // Configuration
      sortKey: 'RELEVANCE',
      reverse: false,
      limit: 24,
      
      // Sort options from provider
      sortOptions: [],
      activeSortOption: null,

      // Pending URL filters for providers where urlFiltersApplicableDirectly === false
      // (resolved after the first load returns filter data)
      pendingUrlFilters: [],

      // True after the first successful results load
      hasLoaded: false,

      /**
       * Initialise the search store
       * Load the configured provider synchronously
       */
      init() {
        try {
          // Get provider from theme settings
          const providerName = window.search?.provider || 'shopify';
          this.loadProvider(providerName);
          
          // Dispatch ready event
          window.dispatchEvent(new CustomEvent('search:ready', {
            detail: { provider: this.currentProviderName }
          }));
        } catch (error) {
          console.error('[Search] Failed to initialise:', error);
          this.error = 'Failed to initialise search. Please refresh the page.';
        }
      },

      /**
       * Load a search provider by name.
       */
      loadProvider(providerName) {
        if (this.provider && this.currentProviderName === providerName) {
          return Promise.resolve(this.provider);
        }

        if (this.providerLoadPromise) {
          return this.providerLoadPromise;
        }

        this.loading = true;

        this.providerLoadPromise = createProvider(providerName)
          .then((provider) => this._onProviderLoaded(provider, providerName))
          .catch((error) => {
            console.error(`[Search] Failed to load provider "${providerName}":`, error);
            this.error = 'Failed to load search provider. Please refresh the page.';
            this.loading = false;
            return null;
          })
          .finally(() => {
            this.providerLoadPromise = null;
          });

        return this.providerLoadPromise;
      },

      /**
       * Finish wiring a successfully-loaded provider instance.
       */
      _onProviderLoaded(provider, providerName) {
        if (!provider.isConfigValid()) {
          console.error(`[Search] Provider "${providerName}" configuration is invalid`);
          this.error = 'Failed to load search provider. Please refresh the page.';
          this.loading = false;
          return;
        }
        this.provider = provider;
        this.currentProviderName = providerName;
        this.restoreUrlState();
        this.loading = false;

        // Defer default predictive prefetch so it doesn't compete with LCP-critical work.
        this.scheduleDefaultResultsPrefetch();
      },

      /**
       * Ensure default predictive results are available (deduplicated).
       */
      ensureDefaultResults(query = window.search?.predictiveDefaultQuery, options = {}) {
        const normalizedQuery = typeof query === 'string' ? query.trim() : '';
        if (!normalizedQuery) return Promise.resolve(null);

        if (!this.provider) {
          return this._waitForProvider(() => this.ensureDefaultResults(normalizedQuery, options), 100);
        }

        const hasDefaultResults = (
          (this.defaultResults.queries?.length || 0) > 0 ||
          (this.defaultResults.collections?.length || 0) > 0 ||
          (this.defaultResults.pages?.length || 0) > 0
        );

        if (hasDefaultResults && this.defaultResultsQuery === normalizedQuery) {
          return Promise.resolve(this.defaultResults);
        }

        const shouldDefer = options.defer !== undefined ? !!options.defer : true;
        if (shouldDefer) {
          return this.scheduleDefaultResultsPrefetch(normalizedQuery);
        }

        return this._fetchDefaultResults(normalizedQuery);
      },

      /**
       * Schedule default predictive prefetch after load/idle to avoid impacting LCP.
       */
      scheduleDefaultResultsPrefetch(query = window.search?.predictiveDefaultQuery) {
        const normalizedQuery = typeof query === 'string' ? query.trim() : '';
        if (!normalizedQuery) return Promise.resolve(null);

        if (!this.provider) {
          return this._waitForProvider(() => this.scheduleDefaultResultsPrefetch(normalizedQuery), 100);
        }

        const hasDefaultResults = (
          (this.defaultResults.queries?.length || 0) > 0 ||
          (this.defaultResults.collections?.length || 0) > 0 ||
          (this.defaultResults.pages?.length || 0) > 0
        );

        if (hasDefaultResults && this.defaultResultsQuery === normalizedQuery) {
          return Promise.resolve(this.defaultResults);
        }

        if (
          this.defaultResultsLoading &&
          this.defaultResultsQuery === normalizedQuery &&
          this.defaultResultsPromise
        ) {
          return this.defaultResultsPromise;
        }

        if (this.defaultResultsPrefetchScheduled && this.defaultResultsDeferredPromise) {
          return this.defaultResultsDeferredPromise;
        }

        this.defaultResultsPrefetchScheduled = true;

        this.defaultResultsDeferredPromise = new Promise((resolve) => {
          const runPrefetch = () => {
            this.defaultResultsPrefetchScheduled = false;
            resolve(this._fetchDefaultResults(normalizedQuery));
          };

          const queueAtIdle = () => {
            if (typeof window.requestIdleCallback === 'function') {
              window.requestIdleCallback(runPrefetch, { timeout: 3000 });
            } else {
              setTimeout(runPrefetch, 1000);
            }
          };

          if (document.readyState === 'complete') {
            queueAtIdle();
          } else {
            window.addEventListener('load', queueAtIdle, { once: true });
          }
        }).finally(() => {
          this.defaultResultsDeferredPromise = null;
        });

        return this.defaultResultsDeferredPromise;
      },

      /**
       * Pre-fetch default suggestion results using the configured default query.
       * Results are stored in defaultResults and do not affect predictiveResults.
       */
      async _fetchDefaultResults(query) {
        const normalizedQuery = typeof query === 'string' ? query.trim() : '';
        if (!normalizedQuery || !this.provider) return null;

        if (
          this.defaultResultsLoading &&
          this.defaultResultsQuery === normalizedQuery &&
          this.defaultResultsPromise
        ) {
          return this.defaultResultsPromise;
        }

        this.defaultResultsQuery = normalizedQuery;

        this.defaultResultsPromise = (async () => {
          this.defaultResultsLoading = true;
          const rawResults = await this.provider.predictiveSearch(normalizedQuery, {
            types: ['queries', 'collections', 'pages'],
            limit: 10
          });
          const transformed = this.provider.transformPredictiveResults(rawResults);
          this.defaultResults = {
            queries: transformed.queries || [],
            collections: transformed.collections || [],
            pages: transformed.pages || []
          };
          return this.defaultResults;
        })().catch((error) => {
          console.error('[Search] Failed to fetch default results:', error);
          return null;
        }).finally(() => {
          this.defaultResultsLoading = false;
        });

        return this.defaultResultsPromise;
      },

      /**
       * Perform a search
       */
      async search(query, options = {}) {
        if (this.isSearchRedirecting) return { redirected: true };
        if (!this.provider) return this._waitForProvider(() => this.search(query, options));

        const searchKey = JSON.stringify({
          query,
          options: this._buildQueryOptions(options)
        });
        if (this.activeSearchPromise && this.activeSearchKey === searchKey) {
          return this.activeSearchPromise;
        }

        const requestId = ++this.searchRequestId;
        this.activeSearchRequestId = requestId;
        this.activeSearchKey = searchKey;
        this.activeSearchPromise = (async () => {
          this.loading = true;
          this.error = null;
          this.query = query;

          // For page-based providers, fetch the target page directly
          const directPage = this.restoredPage > 1 && this.provider.supportsDirectPageFetch
            ? this.restoredPage
            : 1;
          this.currentPage = directPage;
          if (directPage > 1) {
            this.restoredPage = null; // Consumed — prevents _restorePage from running
          }

          const searchOptions = this._buildQueryOptions({ ...options, page: directPage });
          const rawResults = await this.provider.search(query, searchOptions);

          // Ignore stale responses when a newer search request is already active.
          if (requestId !== this.activeSearchRequestId) {
            return null;
          }

          const transformedResults = this.provider.transform(rawResults);

          if (this._shouldRedirectSearch(transformedResults.redirect, requestId, options, directPage)) {
            const redirectUrl = this._prepareSearchRedirectUrl(transformedResults.redirect);
            this.isSearchRedirecting = true;
            const currentUrl = new URL(window.location.href);
            const isResultsPageForThisQuery = currentUrl.pathname === '/search' &&
              (currentUrl.searchParams.get('q') || '').trim() === query;

            if (isResultsPageForThisQuery) {
              window.location.replace(redirectUrl);
            } else {
              window.location.assign(redirectUrl);
            }
            return { ...transformedResults, redirected: true };
          }

          this.updateResultsState(transformedResults);
          if (this._shouldLazyLoadFilterResults() && this.filters.length === 0) {
            this._scheduleDeferredFilterResults(requestId);
          }
          await this._ensureSortOptions(transformedResults);
          this._resetCategoryResults();

          if (this.provider.getSearchCollections && this.provider.getSearchPages) {
            this._categoryResultsStale = true;
            if (this._shouldLazyLoadCategoryResults()) {
              this._scheduleDeferredCategoryResults(requestId);
            } else {
              this._scheduleDeferredCategoryResults(requestId, 0);
            }
          }
          
          // Dispatch search completed event
          window.dispatchEvent(new CustomEvent('search:completed', {
            detail: {
              query,
              results: this.results,
              total: transformedResults.total,
              categoryResults: this.categoryResults,
              provider: this.currentProviderName
            }
          }));

          return transformedResults;
        })();

        try {
          return await this.activeSearchPromise;
        } catch (error) {
          if (requestId !== this.activeSearchRequestId) {
            return null;
          }

          console.error('[Search] Search failed:', error);
          this.error = `Search failed: ${error.message}`;
          
          // Dispatch error event
          window.dispatchEvent(new CustomEvent('search:error', {
            detail: {
              query,
              error: error.message,
              provider: this.currentProviderName
            }
          }));
          
          throw error;

        } finally {
          if (requestId === this.activeSearchRequestId && !this.isSearchRedirecting) {
            this.loading = false;
            this.activeSearchPromise = null;
            this.activeSearchKey = '';
          }
        }
      },

      /**
       * Get products from a collection
       */
      async getCollection(handle, options = {}) {
        if (!this.provider) return this._waitForProvider(() => this.getCollection(handle, options));

        try {
          this.loading = true;
          this.error = null;
          this.isCollectionMode = true;
          this.collectionHandle = handle;
          this.collectionBanners = [];
          this.query = ''; // Clear search query when viewing collection

          // For page-based providers (e.g. SearchSpring), fetch the target page directly
          // so navigating to ?page=N only requires a single API call.
          const directPage = this.restoredPage > 1 && this.provider.supportsDirectPageFetch
            ? this.restoredPage
            : 1;
          this.currentPage = directPage;
          if (directPage > 1) {
            this.restoredPage = null; // Consumed — prevents _restorePage from running
          }

          const collectionOptions = this._buildQueryOptions({ ...options, page: directPage });
          const rawResults = await this.provider.getCollection(handle, collectionOptions);
          const transformedResults = this.provider.transform(rawResults);

          this.updateResultsState(transformedResults);
          await this._ensureSortOptions(transformedResults);

          // Dispatch collection loaded event
          window.dispatchEvent(new CustomEvent('collection:loaded', {
            detail: {
              handle,
              collection: this.collectionInfo,
              results: this.results,
              total: transformedResults.total,
              provider: this.currentProviderName
            }
          }));

          return transformedResults;
        } catch (error) {
          console.error('[Search] Collection fetch failed:', error);
          this.error = `Collection load failed: ${error.message}`;
          
          // Dispatch error event
          window.dispatchEvent(new CustomEvent('collection:error', {
            detail: {
              handle,
              error: error.message,
              provider: this.currentProviderName
            }
          }));
          
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Load more collection results (pagination)
       */
      async loadMoreCollection() {
        if (!this.collectionHandle) return;
        return this._loadMorePage(
          (cursor, opts) => this.provider.loadMoreCollection(cursor, opts),
          'collection:loadMoreCompleted',
          { collection: this.collectionInfo }
        );
      },

      /**
       * Apply filters to current collection or search
       */
      async applyFilters(filters) {
        this.appliedFilters = filters;
        this.currentPage = 1;

        this.preserveFiltersDuringApply = true;

        try {
          if (this.isCollectionMode && this.collectionHandle) {
            await this.getCollection(this.collectionHandle, { filters });
          } else if (this.query) {
            await this.search(this.query, { filters });
          }
        } finally {
          this.preserveFiltersDuringApply = false;
        }

        this.syncUrl();
      },

      /**
       * Set active tab for search results
       */
      setActiveTab(tab) {
        this.activeTab = tab;
        if (this._categoryResultsStale && (tab === 'collections' || tab === 'pages')) {
          this._loadCategoryResults().catch(() => {});
        }
        window.dispatchEvent(new CustomEvent('search:tabChanged', {
          detail: { activeTab: tab }
        }));
      },

      /**
       * Clear current results and reset state
       */
      clearResults() {
        this.results = [];
        this.filters = [];
        this.appliedFilters = [];
        this.query = '';
        this.collectionHandle = '';
        this.collectionInfo = null;
        this.collectionBanners = [];
        this.isCollectionMode = false;
        this.cursor = null;
        this.hasNextPage = false;
        this.error = null;
        this.currentPage = 1;
        this.restoredPage = null;
        this.activeTab = 'products';
        this._categoryResultsStale = false;
        this._categoryResultsLoading = false;
        this._clearDeferredCategoryResults();
        this._filtersLoading = false;
        this._clearDeferredFilterResults();
        this._resetCategoryResults();
      },

      /**
       * Switch to search mode
       */
      switchToSearchMode() {
        this.isCollectionMode = false;
        this.collectionHandle = '';
        this.collectionInfo = null;
        this.collectionBanners = [];
        this.sortKey = 'RELEVANCE'; // Default search sort
      },

      /**
       * Switch to collection mode
       */
      switchToCollectionMode() {
        this.isCollectionMode = true;
        this.query = '';
        this.sortKey = 'MANUAL'; // Default collection sort
      },

      /**
       * Load more results (unified method)
       */
      async loadMore() {
        let result;
        if (this.isCollectionMode) {
          result = await this.loadMoreCollection();
        } else {
          result = await this.loadMoreSearch();
        }
        if (result !== undefined) {
          this.currentPage++;
          this.syncUrl();
        }
        return result;
      },

      /**
       * Load more search results
       */
      async loadMoreSearch() {
        return this._loadMorePage(
          (cursor, opts) => this.provider.loadMore(cursor, opts),
          'search:loadMoreCompleted'
        );
      },

      /**
       * Load more collections results
       */
      async loadMoreCollections() {
        if (!this.categoryPagination.collections.hasNextPage || this.loading) return;
        if (!this.provider?.getSearchCollections) {
          console.warn('Provider does not support collections pagination');
          return;
        }

        try {
          this.loading = true;
          this.error = null;

          const nextPage = this.categoryPagination.collections.currentPage + 1;
          const loadMoreOptions = this._buildQueryOptions({ page: nextPage });
          const results = await this.provider.getSearchCollections(this.query, {
            ...loadMoreOptions,
            page: nextPage
          });

          // Append new results to existing collections
          this.categoryResults.collections = [...this.categoryResults.collections, ...results.results];
          
          // Update pagination state
          this.categoryPagination.collections = {
            cursor: results.cursor,
            hasNextPage: results.hasNextPage,
            total: results.total,
            totalPages: results.totalPages || 1,
            currentPage: nextPage
          };

          window.dispatchEvent(new CustomEvent('search:collectionsLoadMoreCompleted', {
            detail: {
              newResults: results.results,
              totalResults: this.categoryResults.collections.length,
              hasMore: results.hasNextPage
            }
          }));

          return results;
        } catch (error) {
          console.error('[Search] Load more collections failed:', error);
          this.error = `Failed to load more collections: ${error.message}`;
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Load more pages results
       */
      async loadMorePages() {
        if (!this.categoryPagination.pages.hasNextPage || this.loading) return;
        if (!this.provider?.getSearchPages) {
          console.warn('Provider does not support pages pagination');
          return;
        }

        try {
          this.loading = true;
          this.error = null;

          const nextPage = this.categoryPagination.pages.currentPage + 1;
          const loadMoreOptions = this._buildQueryOptions({ page: nextPage });
          const results = await this.provider.getSearchPages(this.query, {
            ...loadMoreOptions,
            page: nextPage
          });

          // Append new results to existing pages
          this.categoryResults.pages = [...this.categoryResults.pages, ...results.results];
          
          // Update pagination state
          this.categoryPagination.pages = {
            cursor: results.cursor,
            hasNextPage: results.hasNextPage,
            total: results.total,
            totalPages: results.totalPages || 1,
            currentPage: nextPage
          };

          window.dispatchEvent(new CustomEvent('search:pagesLoadMoreCompleted', {
            detail: {
              newResults: results.results,
              totalResults: this.categoryResults.pages.length,
              hasMore: results.hasNextPage
            }
          }));

          return results;
        } catch (error) {
          console.error('[Search] Load more pages failed:', error);
          this.error = `Failed to load more pages: ${error.message}`;
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Go to a specific page (numbered pagination)
       */
      async goToPage(n) {
        if (!this.provider) return this._waitForProvider(() => this.goToPage(n));

        const page = Math.max(1, Math.min(n, this.totalPages));
        try {
          this.isPaginating = true;
          this.loading = true;
          this.error = null;
          this.currentPage = page;

          const opts = this._buildQueryOptions({ page });
          let rawResults;
          if (this.isCollectionMode && this.collectionHandle) {
            rawResults = await this.provider.getCollection(this.collectionHandle, opts);
          } else {
            rawResults = await this.provider.search(this.query, opts);
          }
          const transformedResults = this.provider.transform(rawResults);

          this.results = transformedResults.products || [];
          const nextFilters = transformedResults.filters || [];
          if (nextFilters.length > 0 || !this._shouldKeepExistingFilters()) {
            this.filters = nextFilters;
          }
          this.cursor = transformedResults.cursor;
          this.hasNextPage = transformedResults.hasNextPage || false;
          this.total = transformedResults.total || this.total;
          this.collectionBanners = this.isCollectionMode && Array.isArray(transformedResults.banners)
            ? transformedResults.banners
            : [];

          window.dispatchEvent(new CustomEvent('search:pageChanged', {
            detail: {
              page,
              total: this.total,
              provider: this.currentProviderName
            }
          }));

          this.syncUrl();
          return transformedResults;
        } catch (error) {
          console.error('[Search] Go to page failed:', error);
          this.error = `Failed to load page ${page}: ${error.message}`;
          throw error;
        } finally {
          this.loading = false;
          this.isPaginating = false;
        }
      },

      /**
       * Remove a specific filter value
       */
      async removeFilter(filterId, valueId) {
        try {
          const currentFilters = [...(this.appliedFilters || [])];
          
          // Find the filter
          const filterIndex = currentFilters.findIndex(f => f.id === filterId);
          
          if (filterIndex === -1) return;
          
          const filter = currentFilters[filterIndex];
          
          // Remove the specific value
          if (valueId) {
            filter.values = filter.values.filter(value => value.id !== valueId);
            
            // Remove the entire filter if no values left
            if (filter.values.length === 0) {
              currentFilters.splice(filterIndex, 1);
            }
          } else {
            // Remove entire filter
            currentFilters.splice(filterIndex, 1);
          }
          
          // Apply the updated filters
          await this.applyFilters(currentFilters);
          
        } catch (error) {
          console.error('[Search] Failed to remove filter:', error);
          throw error;
        }
      },

      /**
       * Clear all active filters
       */
      async clearAllFilters() {
        try {
          await this.applyFilters([]);
          
          // Dispatch event
          window.dispatchEvent(new CustomEvent('search:filtersCleared', {
            detail: {
              provider: this.currentProviderName
            }
          }));
          
        } catch (error) {
          console.error('[Search] Failed to clear filters:', error);
          throw error;
        }
      },

      // ============================================================
      // PRIVATE HELPERS
      // ============================================================

      /**
       * Returns a comma-separated string of SKUs currently in the cart.
       */
      _getCartSkus() {
        try {
          const cartStore = window.Alpine?.store?.('cart');
          if (!cartStore?.items?.length) return '';
          return cartStore.items.map(item => item.sku).filter(Boolean).join(',');
        } catch {
          return '';
        }
      },

      /**
       * Returns a comma-separated string of recently viewed SKUs from the ss_lastViewed cookie.
       */
      _getLastViewedSkus() {
        try {
          const cookie = document.cookie.split('; ').find(row => row.startsWith('ss_lastViewed='));
          return cookie ? decodeURIComponent(cookie.split('=')[1]) : '';
        } catch {
          return '';
        }
      },

      /**
       * Wait for the provider to become available, then call fn().
       * Handles calls made before async provider loading completes.
       */
      _waitForProvider(fn, interval = 50) {
        return new Promise((resolve) => {
          const check = () => {
            if (this.provider) {
              resolve(fn());
            } else if (this.error) {
              this.error = 'Search provider not available. Please refresh the page.';
              resolve();
            } else {
              setTimeout(check, interval);
            }
          };
          check();
        });
      },

      /**
       * Build standard query options, falling back to store defaults.
       * filters uses the passed value only when explicitly provided so that
       * an undefined argument doesn't overwrite restored URL filters.
       */
      _buildQueryOptions(options) {
        const base = {
          limit: options.limit || this.limit,
          sortKey: options.sortKey || this.sortKey,
          reverse: options.reverse !== undefined ? options.reverse : this.reverse,
          filters: options.filters !== undefined ? options.filters : this.appliedFilters,
          page: options.page !== undefined ? options.page : 1
        };

        if (options.imageWidth) base.imageWidth = options.imageWidth;
        if (options.imageHeight) base.imageHeight = options.imageHeight;

        // Merge provider-specific sort params from activeSortOption so that
        // providers like SearchSpring (which use sortField/sortDirection rather than
        // sortKey/reverse) have their sort applied on the initial page load.
        if (this.activeSortOption && this.provider) {
          Object.assign(base, this.provider.buildSortOptions(this.activeSortOption));
        }

        return base;
      },

      /**
       * Ensure sort options are populated after a results load.
       * Uses sort options embedded in the results when available,
       * otherwise falls back to a separate getSortOptions() fetch.
       */
      async _ensureSortOptions(transformedResults) {
        if (transformedResults.sortOptions?.length > 0) {
          this.sortOptions = transformedResults.sortOptions;
        } else if (this.sortOptions.length === 0) {
          // Only fetch from provider when no sort options are cached.
          // Sort options don't change between pages for the same mode.
          try {
            await this.getSortOptions();
          } catch (error) {
            console.warn('[Search] Failed to get sort options:', error);
          }
        }
      },

      /**
       * Shared pagination logic for both search and collection load-more.
       * @param {Function} providerFn  (cursor, opts) => rawResults
       * @param {string}   eventName   CustomEvent name to dispatch on success
       * @param {Object}   extraDetail Additional fields merged into the event detail
       */
      async _loadMorePage(providerFn, eventName, extraDetail = {}) {
        if (!this.hasNextPage || !this.cursor || this.loadingMore) return;
        if (!this.provider) return this._waitForProvider(() => this._loadMorePage(providerFn, eventName, extraDetail));

        try {
          this.loadingMore = true;
          this.error = null;

          const opts = {
            query: this.query,
            handle: this.collectionHandle,
            limit: this.limit,
            sortKey: this.sortKey,
            reverse: this.reverse,
            filters: this.appliedFilters
          };

          const rawResults = await providerFn(this.cursor, opts);
          const transformedResults = this.provider.transform(rawResults);

          this.results = [...this.results, ...(transformedResults.products || [])];
          this.cursor = transformedResults.cursor;
          this.hasNextPage = transformedResults.hasNextPage || false;

          window.dispatchEvent(new CustomEvent(eventName, {
            detail: {
              newResults: transformedResults.products,
              totalResults: this.results.length,
              hasMore: this.hasNextPage,
              ...extraDetail
            }
          }));

          return transformedResults;
        } catch (error) {
          console.error('[Search] Load more failed:', error);
          this.error = `Failed to load more results: ${error.message}`;
          throw error;
        } finally {
          this.loadingMore = false;
        }
      },

      /**
       * Run the current search or collection query through the provider and
       * return transformed results. Used where results need refreshing without
       * the full state-setup logic of the public search()/getCollection() methods.
       */
      async _runCurrentQuery(options) {
        if (this.isCollectionMode && this.collectionHandle) {
          return this.provider.transform(
            await this.provider.getCollection(this.collectionHandle, options)
          );
        }
        return this.provider.transform(
          await this.provider.search(this.query, options)
        );
      },

      /**       
       * Update results state from transformed provider results
       */
      updateResultsState(transformedResults) {
        this.hasLoaded = true;
        this.results = transformedResults.products || [];
        
        const nextFilters = transformedResults.filters || [];
        const shouldPreserve = this.preserveFiltersDuringApply && this.filters.length > 0;
        
        if (!shouldPreserve && (nextFilters.length > 0 || !this._shouldKeepExistingFilters())) {
          this.filters = nextFilters;
        }

        this.cursor = transformedResults.cursor;
        this.hasNextPage = transformedResults.hasNextPage || false;
        this.total = transformedResults.total || 0;
        
        // Update sort options if provided
        if (transformedResults.sortOptions) {
          this.sortOptions = transformedResults.sortOptions;
        }
        
        // Update collection info if provided
        if (transformedResults.collection) {
          this.collectionInfo = transformedResults.collection;
        }

        this.collectionBanners = this.isCollectionMode && Array.isArray(transformedResults.banners)
          ? transformedResults.banners
          : [];

        // Apply any pending URL filters (two-phase for providers where urlFiltersApplicableDirectly === false)
        if (this.pendingUrlFilters && this.pendingUrlFilters.length > 0) {
          setTimeout(() => this.applyPendingUrlFilters(), 0);
          // _restorePage() will be called at the end of applyPendingUrlFilters
        } else if (this.restoredPage > 1) {
          setTimeout(() => this._restorePage(), 0);
        }
      },

      // ============================================================
      // URL STATE SYNCHRONISATION
      // ============================================================

      /**
       * Sync current filter + sort state into the page URL.
       * Delegates format-specific serialisation to the active provider.
       */
      syncUrl() {
        if (!this.provider) return;

        const hasActiveFilters = this.appliedFilters.length > 0;
        const hasNonDefaultSort = this.activeSortOption && !this.isDefaultSort(this.activeSortOption);

        let newUrl = (hasActiveFilters || hasNonDefaultSort)
          ? this.provider.buildFilterUrl(
              window.location.href,
              this.appliedFilters,
              this.activeSortOption,
              opt => this.isDefaultSort(opt)
            )
          : this.provider.clearFilterUrl(window.location.href);

        // Add or remove page param
        const urlObj = new URL(newUrl);
        if (this.currentPage > 1) {
          urlObj.searchParams.set('page', this.currentPage);
        } else {
          urlObj.searchParams.delete('page');
        }
        newUrl = urlObj.toString();

        window.history.pushState({}, '', newUrl);
      },

      /**
       * Read URL state and pre-populate store with sort + filters.
       * Must be called before the initial collection/search load.
       * Delegates format-specific parsing to the active provider.
       *
       * Providers where urlFiltersApplicableDirectly === true:
       *   filters are applied immediately.
       * Providers where urlFiltersApplicableDirectly === false:
       *   sort is applied immediately; filters are stored as pending and
       *   resolved after the first load returns available filter data.
       */
      restoreUrlState() {
        if (!this.provider) return;

        // Restore page number (provider-agnostic)
        const pageParam = parseInt(new URL(window.location.href).searchParams.get('page'));
        if (pageParam > 1) {
          this.restoredPage = pageParam;
        }

        const parsed = this.provider.parseUrlState();

        // Apply sort immediately
        if (parsed.sortOption) {
          this.activeSortOption = parsed.sortOption;
          if (parsed.sortOption.sortKey) {
            this.sortKey = parsed.sortOption.sortKey;
            this.reverse = parsed.sortOption.reverse;
          }
          // SearchSpring sort has no sortKey — sortKey/reverse are unused by that provider
        }

        if (parsed.filters.length > 0) {
          if (this.provider.urlFiltersApplicableDirectly) {
            this.appliedFilters = parsed.filters;
          } else {
            this.pendingUrlFilters = parsed.filters;
          }
        }
      },

      /**
       * Apply pending URL filters after the initial load has returned filter data.
       * Used by providers where urlFiltersApplicableDirectly === false,
       * where filter value objects must be resolved from the loaded filter catalogue
       * before a filtered request can be made.
       */
      async applyPendingUrlFilters() {
        if (!this.pendingUrlFilters || this.pendingUrlFilters.length === 0) return;

        const pending = this.pendingUrlFilters;
        this.pendingUrlFilters = [];

        const matched = [];
        for (const pendingFilter of pending) {
          const filterData = this.filters.find(f => f.id === pendingFilter.id);
          if (!filterData) continue;

          const matchedValues = pendingFilter.values
            .map(pendingValue =>
              filterData.values.find(v =>
                v.label.toLowerCase() === pendingValue.label.toLowerCase()
              )
            )
            .filter(Boolean);

          if (matchedValues.length > 0) {
            matched.push({ id: filterData.id, label: filterData.label, type: filterData.type, values: matchedValues });
          }
        }

        if (matched.length === 0) {
          // No filters matched, but still restore page if needed
          if (this.restoredPage > 1) await this._restorePage();
          return;
        }

        this.appliedFilters = matched;

        // Reload with matched filters but skip URL sync (URL is already correct)
        try {
          this.loading = true;
          const options = {
            limit: this.limit,
            sortKey: this.sortKey,
            reverse: this.reverse,
            filters: matched
          };

          if (!this.isCollectionMode && !this.query) return;
          // Update results only — do NOT call updateResultsState to avoid re-triggering pending loop
          const transformed = await this._runCurrentQuery(options);
          this.results = transformed.products || [];
          this.filters = transformed.filters || [];
          this.cursor = transformed.cursor;
          this.hasNextPage = transformed.hasNextPage || false;
          if (transformed.sortOptions) this.sortOptions = transformed.sortOptions;
          if (transformed.collection) this.collectionInfo = transformed.collection;
          this.collectionBanners = this.isCollectionMode && Array.isArray(transformed.banners)
            ? transformed.banners
            : [];
        } catch (error) {
          console.error('[Search] Failed to apply URL state filters:', error);
        } finally {
          this.loading = false;
        }

        // Restore page after filters have been applied
        if (this.restoredPage > 1) {
          await this._restorePage();
        }
      },

      /**
       * Restore to a previously-visited page by sequentially loading pages.
       * Called after the initial load (and any pending URL filter resolution) completes.
       * Does not trigger URL sync — the URL already has the correct page param.
       */
      async _restorePage() {
        if (!this.restoredPage || this.restoredPage <= 1) return;
        const targetPage = this.restoredPage;
        this.restoredPage = null;

        for (let page = 2; page <= targetPage; page++) {
          if (!this.hasNextPage) break;
          if (this.isCollectionMode) {
            await this.loadMoreCollection();
          } else {
            await this.loadMoreSearch();
          }
          this.currentPage = page;
        }
        // URL is already correct — no syncUrl() needed
      },

      /**
       * Returns true if the given sort option is the default for the current mode.
       * Delegates to the active provider.
       */
      isDefaultSort(sortOption) {
        if (!sortOption) return true;
        if (!this.provider) return true;
        return this.provider.isDefaultSort(sortOption, this.isCollectionMode);
      },

      /**
       * Get available sort options from the current provider
       */
      async getSortOptions() {
        if (!this.provider) {
          console.warn('Search provider not available for sort options');
          return [];
        }

        try {
          const context = {
            query: this.query,
            isCollectionMode: this.isCollectionMode,
            handle: this.collectionHandle
          };
          
          this.sortOptions = await this.provider.getSortOptions(context);
          
          // Set default active sort option if none set
          if (!this.activeSortOption && this.sortOptions.length > 0) {
            this.activeSortOption = this.sortOptions[0];
          }
          
          return this.sortOptions;
        } catch (error) {
          console.error('[Search] Failed to get sort options:', error);
          return [];
        }
      },

      /**
       * Update sort order using provider-specific sort option
       */
      async updateSortByOption(sortOption) {
        if (!this.provider) {
          console.warn('Search provider not available for sort update');
          return;
        }

        try {
          this.loading = true;
          this.activeSortOption = sortOption;
          this.currentPage = 1;
          
          // Build search/collection options based on provider format
          const options = {
            limit: this.limit,
            filters: this.appliedFilters
          };
          
          // Merge provider-specific sort parameters
          Object.assign(options, this.provider.buildSortOptions(sortOption));
          
          // Update legacy sort properties for compatibility
          this.sortKey = sortOption.sortKey || 'RELEVANCE';
          this.reverse = sortOption.reverse || false;

          const isCategoryTabSort = (
            !this.isCollectionMode &&
            (this.activeTab === 'collections' || this.activeTab === 'pages')
          );

          if (isCategoryTabSort) {
            await this._updateCategoryTabResults(options);
          } else {
            // Re-run appropriate query based on current mode
            const transformedResults = await this._runCurrentQuery(options);
            this.updateResultsState(transformedResults);
            await this._ensureSortOptions(transformedResults);
          }

          this.syncUrl();

          // Dispatch sort updated event
          window.dispatchEvent(new CustomEvent('search:sortUpdated', {
            detail: {
              sortOption: this.activeSortOption,
              sortKey: this.sortKey,
              reverse: this.reverse,
              isCollectionMode: this.isCollectionMode
            }
          }));
        } catch (error) {
          console.error('[Search] Sort update failed:', error);
          this.error = `Failed to update sort order: ${error.message}`;
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Update sort order and re-run current search/collection (legacy method)
       */
      async updateSort(sortKey, reverse = false) {
        // Find matching sort option for legacy support
        const sortOption = this.sortOptions.find(opt => 
          opt.sortKey === sortKey && opt.reverse === reverse
        );
        
        if (sortOption) {
          return await this.updateSortByOption(sortOption);
        }
        
        // Fallback to legacy behavior
        if (!this.provider) {
          console.warn('Search provider not available for sort update');
          return;
        }

        try {
          // Update sort settings
          this.sortKey = sortKey;
          this.reverse = reverse;

          // Re-run appropriate query based on current mode
          if (this.isCollectionMode && this.collectionHandle) {
            await this.getCollection(this.collectionHandle, {
              limit: this.limit,
              sortKey: this.sortKey,
              reverse: this.reverse,
              filters: this.appliedFilters
            });
          } else if (this.query) {
            await this.search(this.query, {
              limit: this.limit,
              sortKey: this.sortKey,
              reverse: this.reverse,
              filters: this.appliedFilters
            });
          }

          this.syncUrl();

          // Dispatch sort updated event
          window.dispatchEvent(new CustomEvent('search:sortUpdated', {
            detail: {
              sortKey: this.sortKey,
              reverse: this.reverse,
              isCollectionMode: this.isCollectionMode
            }
          }));
        } catch (error) {
          console.error('[Search] Sort update failed:', error);
          this.error = `Failed to update sort order: ${error.message}`;
          throw error;
        }
      },

      /**
       * Refresh category tab results (collections/pages) with current query + sort.
       */
      async _updateCategoryTabResults(options = {}) {
        if (!this.provider) return;
        if (!this.query || this.query.trim().length === 0) return;

        if (this.activeTab === 'collections' && this.provider.getSearchCollections) {
          const response = await this.provider.getSearchCollections(this.query, {
            ...options,
            page: 1
          });

          this.categoryResults.collections = response?.results || [];
          this.categoryPagination.collections = {
            cursor: response?.cursor || null,
            hasNextPage: response?.hasNextPage || false,
            total: response?.total || this.categoryResults.collections.length,
            totalPages: response?.totalPages || 1,
            currentPage: 1
          };
          return;
        }

        if (this.activeTab === 'pages' && this.provider.getSearchPages) {
          const response = await this.provider.getSearchPages(this.query, {
            ...options,
            page: 1
          });

          this.categoryResults.pages = response?.results || [];
          this.categoryPagination.pages = {
            cursor: response?.cursor || null,
            hasNextPage: response?.hasNextPage || false,
            total: response?.total || this.categoryResults.pages.length,
            totalPages: response?.totalPages || 1,
            currentPage: 1
          };
        }
      },

      /**
       * Lazy-load collections and pages for category tabs.
       */
      async _loadCategoryResults() {
        if (!this.provider?.getSearchCollections || !this.provider?.getSearchPages) return;
        if (!this.query || this.query.trim().length === 0) return;
        if (this._categoryResultsLoading) return;

        const requestId = this.activeSearchRequestId;

        try {
          this._categoryResultsLoading = true;
          this.error = null;
          this._categoryResultsStale = false;

          const searchOptions = this._buildQueryOptions({ page: 1 });
          const [collectionsResponse, pagesResponse] = await Promise.all([
            this.provider.getSearchCollections(this.query, { ...searchOptions, page: 1 }),
            this.provider.getSearchPages(this.query, { ...searchOptions, page: 1 })
          ]);

          if (requestId !== this.activeSearchRequestId) {
            return null;
          }

          this.categoryResults.collections = collectionsResponse?.results || [];
          this.categoryResults.pages = pagesResponse?.results || [];
          this.categoryPagination.collections = {
            cursor: collectionsResponse?.cursor || null,
            hasNextPage: collectionsResponse?.hasNextPage || false,
            total: collectionsResponse?.total || this.categoryResults.collections.length,
            totalPages: collectionsResponse?.totalPages || 1,
            currentPage: 1
          };
          this.categoryPagination.pages = {
            cursor: pagesResponse?.cursor || null,
            hasNextPage: pagesResponse?.hasNextPage || false,
            total: pagesResponse?.total || this.categoryResults.pages.length,
            totalPages: pagesResponse?.totalPages || 1,
            currentPage: 1
          };

          window.dispatchEvent(new CustomEvent('search:categoryResultsLoaded', {
            detail: {
              categoryResults: this.categoryResults,
              provider: this.currentProviderName
            }
          }));

          return this.categoryResults;
        } catch (error) {
          console.error('[Search] Category results failed:', error);
          this.error = `Failed to load category results: ${error.message}`;
          this._categoryResultsStale = true;
          this._resetCategoryResults();
          throw error;
        } finally {
          this._categoryResultsLoading = false;
        }
      },

      _resetCategoryResults() {
        this.categoryResults = {
          collections: [],
          pages: []
        };
        this.categoryPagination = {
          collections: {
            cursor: null,
            hasNextPage: false,
            total: 0,
            totalPages: 1,
            currentPage: 1
          },
          pages: {
            cursor: null,
            hasNextPage: false,
            total: 0,
            totalPages: 1,
            currentPage: 1
          }
        };
      },

      _scheduleDeferredCategoryResults(requestId, delay = 1200) {
        this._clearDeferredCategoryResults();
        this._categoryResultsDeferredTimer = window.setTimeout(() => {
          if (requestId !== this.activeSearchRequestId || !this._categoryResultsStale) return;
          this._loadCategoryResults().catch(() => {});
        }, delay);
      },

      _clearDeferredCategoryResults() {
        if (!this._categoryResultsDeferredTimer) return;
        window.clearTimeout(this._categoryResultsDeferredTimer);
        this._categoryResultsDeferredTimer = null;
      },

      _shouldLazyLoadCategoryResults() {
        return typeof this.provider?.isLightProductModel === 'function' && this.provider.isLightProductModel();
      },

      _shouldLazyLoadFilterResults() {
        return typeof this.provider?.isLightProductModel === 'function' && this.provider.isLightProductModel();
      },

      _shouldKeepExistingFilters() {
        return this._shouldLazyLoadFilterResults() && this.filters.length > 0;
      },

      /**
       * Suppresses redirects that are stale, non-submitted, interactive, or already in progress.
       */
      _shouldRedirectSearch(redirect, requestId, options = {}, directPage = 1) {
        if (!redirect || this.isSearchRedirecting) return false;
        if (requestId !== this.activeSearchRequestId) return false;
        if (this.isCollectionMode && !options.submittedSearch) return false;
        if (window.Shopify?.designMode) return false;
        if (directPage !== 1) return false;
        if (options.filters !== undefined) return false;
        if (options.sortKey !== undefined || options.reverse !== undefined) return false;
        if (options.sortField !== undefined || options.sortDirection !== undefined) return false;
        if (options.page !== undefined && Number(options.page) !== 1) return false;

        try {
          const targetUrl = new URL(redirect, window.location.origin);
          const currentUrl = new URL(window.location.href);
          const sameHref = targetUrl.href === currentUrl.href;
          const samePath = targetUrl.origin === currentUrl.origin &&
            targetUrl.pathname.replace(/\/$/, '') === currentUrl.pathname.replace(/\/$/, '');
          return !sameHref && !samePath;
        } catch {
          return false;
        }
      },

      /**
       * Preserves Shopify theme-preview context while preparing a SearchSpring redirect URL.
       */
      _prepareSearchRedirectUrl(redirect) {
        const targetUrl = new URL(redirect, window.location.origin);
        const currentUrl = new URL(window.location.href);
        const theme = window.Shopify?.theme;
        const previewThemeId = currentUrl.searchParams.get('preview_theme_id') ||
          (theme?.role && theme.role !== 'main' ? theme.id : null);

        if (previewThemeId && targetUrl.origin === currentUrl.origin) {
          targetUrl.searchParams.set('preview_theme_id', previewThemeId);
        }

        if (targetUrl.origin === currentUrl.origin) {
          return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
        }

        return targetUrl.href;
      },

      _scheduleDeferredFilterResults(requestId) {
        this._clearDeferredFilterResults();
        this._filtersDeferredTimer = window.setTimeout(() => {
          if (requestId !== this.activeSearchRequestId || this.filters.length > 0) return;
          this._loadFilterResults().catch(() => {});
        }, 1400);
      },

      _clearDeferredFilterResults() {
        if (!this._filtersDeferredTimer) return;
        window.clearTimeout(this._filtersDeferredTimer);
        this._filtersDeferredTimer = null;
      },

      async _loadFilterResults() {
        if (!this.provider) return;
        if (!this.query && !this.collectionHandle) return;
        if (this._filtersLoading) return;

        try {
          this._filtersLoading = true;
          const filters = this.isCollectionMode && this.collectionHandle && this.provider.getCollectionFilters
            ? await this.provider.getCollectionFilters(this.collectionHandle)
            : await this.provider.getFilters(this.query);

          if (Array.isArray(filters) && filters.length > 0) {
            this.filters = filters;
          }
        } catch (error) {
          console.error('[Search] Filter results failed:', error);
        } finally {
          this._filtersLoading = false;
        }
      },

      async goToCategoryPage(tab, page) {
        if (!this.provider) return this._waitForProvider(() => this.goToCategoryPage(tab, page));
        if (!this.query || this.query.trim().length === 0) return;
        if (tab !== 'collections' && tab !== 'pages') return;

        const safePage = Math.max(1, page);

        try {
          this.loading = true;
          this.error = null;

          const opts = this._buildQueryOptions({ page: safePage });
          const response = tab === 'collections'
            ? await this.provider.getSearchCollections(this.query, { ...opts, page: safePage })
            : await this.provider.getSearchPages(this.query, { ...opts, page: safePage });

          if (tab === 'collections') {
            this.categoryResults.collections = response?.results || [];
            this.categoryPagination.collections = {
              cursor: response?.cursor || null,
              hasNextPage: response?.hasNextPage || false,
              total: response?.total || this.categoryResults.collections.length,
              totalPages: response?.totalPages || 1,
              currentPage: safePage
            };
          } else {
            this.categoryResults.pages = response?.results || [];
            this.categoryPagination.pages = {
              cursor: response?.cursor || null,
              hasNextPage: response?.hasNextPage || false,
              total: response?.total || this.categoryResults.pages.length,
              totalPages: response?.totalPages || 1,
              currentPage: safePage
            };
          }

          window.dispatchEvent(new CustomEvent('search:categoryPageChanged', {
            detail: {
              tab,
              page: safePage,
              total: tab === 'collections' ? this.categoryPagination.collections.total : this.categoryPagination.pages.total,
              totalPages: tab === 'collections' ? this.categoryPagination.collections.totalPages : this.categoryPagination.pages.totalPages,
              provider: this.currentProviderName
            }
          }));

          return response;
        } catch (error) {
          console.error('[Search] Category page change failed:', error);
          this.error = `Failed to load ${tab} page ${safePage}: ${error.message}`;
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Perform predictive search
       */
      async predictiveSearch(query, options = {}) {
        if (!this.provider) return this._waitForProvider(() => this.predictiveSearch(query, options), 100);

        const requestId = ++this.predictiveRequestId;
        this.predictiveActiveRequestId = requestId;
        if (this.predictiveAbortController) {
          this.predictiveAbortController.abort();
        }

        this.predictiveAbortController = new AbortController();
        const signal = this.predictiveAbortController.signal;

        try {
          this.predictiveLoading = true;
          this.predictiveQuery = query;
          
          // Don't search if query is too short
          if (!query || query.trim().length < 2) {
            this.clearPredictiveResults();
            return;
          }

          // Use enabled types for optimized queries
          const searchOptions = {
            ...options,
            signal,
            types: options.types || this.predictiveEnabledTypes,
            cart: options.cart !== undefined ? options.cart : this._getCartSkus(),
            lastViewed: options.lastViewed !== undefined ? options.lastViewed : this._getLastViewedSkus()
          };

          const rawResults = await this.provider.predictiveSearch(query.trim(), searchOptions);

          // Ignore stale responses when a newer predictive request is already active.
          if (requestId !== this.predictiveActiveRequestId) {
            return this.predictiveResults;
          }

          this.predictiveResults = this.provider.transformPredictiveResults(rawResults);
          this.predictiveTotalResults = rawResults?.pagination.totalResults || 0;
          // Dispatch predictive search completed event
          window.dispatchEvent(new CustomEvent('predictiveSearch:completed', {
            detail: {
              query,
              results: this.predictiveResults,
              enabledTypes: this.predictiveEnabledTypes,
              provider: this.currentProviderName
            }
          }));

          return this.predictiveResults;
        } catch (error) {
          if (error?.name === 'AbortError') {
            return this.predictiveResults;
          }

          if (requestId !== this.predictiveActiveRequestId) {
            return this.predictiveResults;
          }

          console.error('[Search] Predictive search failed:', error);
          
          // Dispatch error event
          window.dispatchEvent(new CustomEvent('predictiveSearch:error', {
            detail: {
              query,
              error: error.message,
              enabledTypes: this.predictiveEnabledTypes,
              provider: this.currentProviderName
            }
          }));
          
          throw error;
        } finally {
          if (requestId === this.predictiveActiveRequestId) {
            this.predictiveLoading = false;
            document.body.classList.remove('search-mobile-waiting');
            this.predictiveAbortController = null;
          }
        }
      },

      /**
       * Clear predictive search results
       */
      clearPredictiveResults() {
        this.predictiveResults = {
          products: [],
          collections: [],
          queries: [],
          articles: [],
          pages: []
        };
        this.predictiveQuery = '';
        this.predictiveTotalResults = 0;
        this.predictiveLoading = false;
        document.body.classList.remove('search-mobile-waiting');
        if (this.predictiveAbortController) {
          this.predictiveAbortController.abort();
          this.predictiveAbortController = null;
        }
      },

      get hasCategoryResultsStale() {
        return this._categoryResultsStale;
      },

      get categoryResultsLoading() {
        return this._categoryResultsLoading;
      },

      get filtersLoading() {
        return this._filtersLoading;
      },

      /**
       * Check if search is ready
       */
      get isReady() {
        return !!(this.provider && this.provider.isConfigValid());
      },

      /**
       * Computed getters for modular predictive search
       */
      get hasQueries() {
        return this.predictiveResults.queries?.length > 0;
      },

      get hasProducts() {
        return this.predictiveResults.products?.length > 0;
      },

      get hasCollections() {
        return this.predictiveResults.collections?.length > 0;
      },

      get hasArticles() {
        return this.predictiveResults.articles?.length > 0;
      },

      get hasPages() {
        return this.predictiveResults.pages?.length > 0;
      },

      get hasAnyPredictiveResults() {
        return this.hasQueries || this.hasProducts || this.hasCollections || this.hasArticles || this.hasPages;
      },

      /**
       * Total number of pages based on total results and page limit
       */
      get totalPages() {
        return this.total > 0 ? Math.ceil(this.total / this.limit) : 1;
      },

      /**
       * Configure enabled types for modular predictive search
       */
      setPredictiveEnabledTypes(types) {
        if (Array.isArray(types)) {
          this.predictiveEnabledTypes = types;
        }
      },

      /**
       * Get current provider display name
       */
      get providerDisplayName() {
        return this.provider?.displayName ?? 'Unknown';
      }
    };
  }
};
